import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { CustomFunction } from '@blaze-shared/api-contracts-domain';
import { ButtonComponent } from '@blaze-shared/shared-ui';

import { OpenAIConnectorFunctionComponent } from '../openai-connector-function/openai-connector-function.component';

interface FunctionRow {
  readonly uid: number;
  function: CustomFunction;
}

let nextRowUid = 0;

@Component({
  selector: 'blaze-openai-connector-functions-container',
  templateUrl: './openai-connector-functions-container.component.html',
  styleUrls: ['./openai-connector-functions-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    OpenAIConnectorFunctionComponent,
    ReactiveFormsModule,
  ],
})
export class OpenAIConnectorFunctionsContainerComponent implements OnChanges {
  @Input() customFunctions?: CustomFunction[] = [];

  @Output() functionsChanged = new EventEmitter<CustomFunction[]>();
  @Output() isValid = new EventEmitter<boolean>();

  private readonly rowsValidity = new Map<number, boolean>();
  private lastEmittedFunctions?: CustomFunction[];

  /**
   * Rebuilt only on external input changes; echoes of own emissions are
   * ignored to keep form state. The uid keeps @for tracking stable
   */
  protected readonly rows = signal<FunctionRow[]>([]);

  ngOnChanges(): void {
    if (this.customFunctions !== this.lastEmittedFunctions) {
      this.rowsValidity.clear();
      this.rows.set(
        (this.customFunctions || []).map((customFunction) => ({
          uid: nextRowUid++,
          function: customFunction,
        })),
      );
    }
  }

  addFunction(): void {
    const row: FunctionRow = {
      uid: nextRowUid++,
      function: {
        name: '',
        modelName: '',
        systemInstruction: '',
        top_p: 1,
        temperature: 1,
        outputSchema: [],
      },
    };
    this.rowsValidity.set(row.uid, false);
    this.rows.update((rows) => [...rows, row]);
    this.emitFunctions();
    this.emitValidity();
  }

  removeFunction(uid: number): void {
    this.rows.update((rows) => rows.filter((row) => row.uid !== uid));
    this.rowsValidity.delete(uid);
    this.emitFunctions();
    this.emitValidity();
  }

  updateFunction(uid: number, functionData: CustomFunction): void {
    this.rows.update((rows) =>
      rows.map((row) =>
        row.uid === uid ? { ...row, function: { ...row.function, ...functionData } } : row,
      ),
    );
    this.emitFunctions();
  }

  setFunctionValidity(uid: number, isValid: boolean): void {
    this.rowsValidity.set(uid, isValid);
    this.emitValidity();
  }

  private emitFunctions(): void {
    this.lastEmittedFunctions = this.rows().map((row) => row.function);
    this.functionsChanged.emit(this.lastEmittedFunctions);
  }

  private emitValidity(): void {
    this.isValid.emit([...this.rowsValidity.values()].every(Boolean));
  }
}
